import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withHandlers, withState, lifecycle } from 'recompose';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';

import {
	getFacilityPhoneNumber,
	getFacilityCardPhoneNumber,
} from 'store/modules/facilities';
import { getTollfreePhoneNumber } from 'store/modules/phone';
import { isSPMedium, isLarge } from 'utils/breakpoints';

import SearchPin from 'components/search/SearchMap/SearchPin';
import { SearchInfoWindow } from 'components/search/SearchMap';
import { NewFacilityCard } from 'components/facility/FacilityCard';
import FacilityCard from 'components/organisms/FacilityCard';

export const enhance = compose(
	connect(
		undefined,
		{
			getFacilityPhoneNumber,
			getTollfreePhoneNumber,
			getFacilityCardPhoneNumber,
		},
		mapDispatchToActions,
	),
	withState('overlayRef', 'setOverlayRef', null),
	withHandlers({
		// when selecting the pin, we should show the mini facility card and select the facility card
		handleSelect:
			({
				handleFacilitySelected,
				handlePinSelected,
				facility,
				google,
				map,
				isNewSearchVariant, // [sf16978Variation]
				onScrollToFacilityCard,
			}) =>
			() => {
				handleFacilitySelected(facility.id);
				handlePinSelected();

				// Pan map on mobile to position marker above bottom card/toolbar
				const hasMapObjects = google && map;
				const viewportWidth = window.innerWidth;
				const shouldAdjustForMobile =
					hasMapObjects &&
					(isNewSearchVariant
						? !isLarge(viewportWidth)
						: !isSPMedium(viewportWidth)); // [sf16978Variation]

				if (shouldAdjustForMobile) {
					const { latitude, longitude } = facility.location;
					const markerLatLng = new google.maps.LatLng(
						latitude,
						longitude,
					);

					// First center on the marker
					map.panTo(markerLatLng);

					// New search variant needs a positive offset; legacy variant uses negative
					const panOffset = isNewSearchVariant ? 270 : -100; // [sf16978Variation]

					setTimeout(() => map.panBy(0, panOffset), 100);
				}
				// Desktop variant: scroll list to facility card instead of showing mini overlay
				if (isNewSearchVariant && isLarge(viewportWidth)) {
					// [sf16978Variation]
					onScrollToFacilityCard &&
						onScrollToFacilityCard(facility.id);
				}
			},
		// when deselecting the pin, we should hide the mini facility card and deselect the facility card
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				handleFacilityDeselected();
				handlePinDeselected();
			},
		handleMouseEnter:
			({ handleFacilitySelected, facility, pinSelected }) =>
			() => {
				if (pinSelected) return;
				handleFacilitySelected(facility.id);
			},
		handleMouseLeave:
			({ handleFacilityDeselected, pinSelected }) =>
			() => {
				if (pinSelected) return;
				handleFacilityDeselected();
			},
		setOverlayNode:
			({ setOverlayRef }) =>
			(node) => {
				setOverlayRef(node);
			},
		outsideClickHandler:
			({
				overlayRef,
				showMiniCard,
				isNewSearchVariant, // [sf16978Variation]
				handleFacilityDeselected,
				handlePinDeselected,
			}) =>
			(event) => {
				if (!showMiniCard || !isNewSearchVariant) return; // only apply for new search variant [sf16978Variation]
				if (isLarge(window.innerWidth)) return; // desktop guard
				if (!overlayRef) return;

				// We are now listening on 'click' so internal button handlers run first.
				// Determine if the click target is inside the overlay.
				let inside = false;
				const target = event.target;

				if (overlayRef.contains(target)) {
					inside = true;
				} else if (typeof event.composedPath === 'function') {
					const path = event.composedPath();
					if (path && path.indexOf(overlayRef) !== -1) inside = true;
				}

				// Fallback: match known facility card wrapper classes from old variant for robustness
				if (!inside) {
					const closestCard =
						target.closest &&
						target.closest(
							'.search-overlay-card, .new-facility-card, .facility-card, .new-clp-facility-card',
						);
					if (closestCard && overlayRef.contains(closestCard))
						inside = true;
				}

				if (!inside) {
					// Outside click -> dismiss
					handleFacilityDeselected();
					handlePinDeselected();
				}
			},
	}),
	lifecycle({
		componentDidMount() {
			this.manageOutsideListener();
		},
		componentDidUpdate(prevProps) {
			if (
				prevProps.showMiniCard !== this.props.showMiniCard ||
				prevProps.overlayRef !== this.props.overlayRef ||
				prevProps.isNewSearchVariant !== this.props.isNewSearchVariant // [sf16978Variation]
			) {
				this.manageOutsideListener();
			}
		},
		componentWillUnmount() {
			this.removeOutsideListener();
		},
		manageOutsideListener() {
			this.removeOutsideListener();
			const { showMiniCard, isNewSearchVariant, overlayRef } = this.props;
			if (
				showMiniCard &&
				isNewSearchVariant && // [sf16978Variation]
				!isLarge(window.innerWidth) &&
				overlayRef
			) {
				this.boundOutsideHandler = this.props.outsideClickHandler;
				document.addEventListener(
					'click',
					this.boundOutsideHandler,
					true,
				); // capture to run before map handlers
				document.addEventListener(
					'touchend',
					this.boundOutsideHandler,
					{ passive: true, capture: true },
				);
			}
		},
		removeOutsideListener() {
			if (this.boundOutsideHandler) {
				document.removeEventListener(
					'click',
					this.boundOutsideHandler,
					true,
				);
				document.removeEventListener(
					'touchend',
					this.boundOutsideHandler,
					true,
				);
				this.boundOutsideHandler = null;
			}
		},
	}),
);
export function SearchMarker(props) {
	return (
		<>
			<SearchPin
				{...props}
				onClick={props.handleSelect}
				onMouseEnter={props.handleMouseEnter}
				onMouseLeave={props.handleMouseLeave}
			/>
			{props.showMiniCard && (
				<>
					{props.isNewSearchVariant ? ( // [sf16978Variation]
						<div
							className="search-overlay-card"
							role="dialog"
							data-overlay-root="facility-overlay"
							ref={props.setOverlayNode}
						>
							<FacilityCard
								{...props}
								withUnits={false}
								responsive={false}
								withSchema={false}
								onVisible={props.handleFacilityCardVisible}
								miniCard
							/>
						</div>
					) : (
						<>
							<NewFacilityCard // here for the mobile version
								{...props}
								baseUrl={props.baseUrl}
								getFacilityCardPhoneNumber={() =>
									props.actions.getFacilityCardPhoneNumber(
										props.location.city,
										props.location.state,
										props.facility.id,
									)
								}
								onVisible={props.handleFacilityCardVisible}
								bottomMap
								miniCard
							/>
							<SearchInfoWindow {...props} />
						</>
					)}
				</>
			)}
		</>
	);
}

SearchMarker.propTypes = {
	selected: PropTypes.bool,
	handleSelect: PropTypes.func.isRequired,
	handleMouseEnter: PropTypes.func.isRequired,
	handleMouseLeave: PropTypes.func.isRequired,
	handleFacilityCardVisible: PropTypes.func.isRequired,
	setOverlayNode: PropTypes.func, // injected by withHandlers for ref assignment
	showMiniCard: PropTypes.bool.isRequired,
	location: PropTypes.shape({
		city: PropTypes.string.isRequired,
		state: PropTypes.string.isRequired,
	}).isRequired,
	facility: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
	baseUrl: PropTypes.string,
	actions: PropTypes.shape({
		getFacilityCardPhoneNumber: PropTypes.func.isRequired,
	}).isRequired,
	isNewSearchVariant: PropTypes.bool,
	onScrollToFacilityCard: PropTypes.func,
};

export default enhance(SearchMarker);
